import { supabase } from "../supabase";
import {
  loadProjectQuoteLines,
  QUOTE_LINE_TYPE,
} from "./projectQuoteSnapshots";

function normalizeMatchText(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

export function findMatchingQuoteLine(quoteLines, {
  lineType,
  materialId = null,
  quoteLineSnapshotId = null,
} = {}) {
  if (!quoteLines?.length) return null;

  if (quoteLineSnapshotId) {
    const byId = quoteLines.find(
      (line) => line.line_snapshot_id === quoteLineSnapshotId,
    );
    if (byId) return byId;
  }

  if (lineType === QUOTE_LINE_TYPE.MATERIAL && materialId) {
    return (
      quoteLines.find(
        (line) =>
          line.line_type === QUOTE_LINE_TYPE.MATERIAL &&
          line.material_id === materialId,
      ) || null
    );
  }

  if (lineType === QUOTE_LINE_TYPE.LABOUR && quoteLineSnapshotId) {
    return (
      quoteLines.find(
        (line) =>
          line.line_type === QUOTE_LINE_TYPE.LABOUR &&
          line.line_snapshot_id === quoteLineSnapshotId,
      ) || null
    );
  }

  return null;
}

export function findMatchingLabourQuoteLine(
  quoteLines = [],
  {
    workCompleted = "",
    workCategory = "",
    tradeName = "",
    quoteLineSnapshotId = null,
  } = {},
) {
  if (!quoteLines?.length) return null;

  if (quoteLineSnapshotId) {
    const byId = findMatchingQuoteLine(quoteLines, {
      lineType: QUOTE_LINE_TYPE.LABOUR,
      quoteLineSnapshotId,
    });
    if (byId) return byId;
  }

  const labourLines = quoteLines.filter(
    (line) => line.line_type === QUOTE_LINE_TYPE.LABOUR,
  );
  if (!labourLines.length) return null;

  const context = normalizeMatchText(
    [workCompleted, workCategory, tradeName].filter(Boolean).join(" "),
  );

  if (context) {
    const descriptionMatch = labourLines.find((line) => {
      const description = normalizeMatchText(line.description_snapshot);
      if (!description) return false;
      if (context.includes(description) || description.includes(context)) {
        return true;
      }

      const descriptionWords = description
        .split(" ")
        .filter((word) => word.length >= 4);
      return descriptionWords.some((word) => context.includes(word));
    });
    if (descriptionMatch) return descriptionMatch;

    const token = context.split(" ")[0];
    if (token.length >= 4) {
      const tokenMatch = labourLines.find((line) =>
        normalizeMatchText(line.description_snapshot).includes(token),
      );
      if (tokenMatch) return tokenMatch;
    }
  }

  if (labourLines.length === 1) {
    return labourLines[0];
  }

  return null;
}

export async function loadProjectQuoteLinesCached(projectId, cache = new Map()) {
  if (!projectId) return [];

  if (!cache.has(projectId)) {
    cache.set(projectId, loadProjectQuoteLines(projectId));
  }

  return cache.get(projectId);
}

export async function resolveMaterialQuoteLineSnapshotId(
  projectId,
  materialId,
  { quoteLines = null, cache = new Map() } = {},
) {
  if (!projectId || !materialId) return null;

  const lines =
    quoteLines ||
    (await loadProjectQuoteLines(projectId, {
      lineType: QUOTE_LINE_TYPE.MATERIAL,
    }));

  const match = findMatchingQuoteLine(lines, {
    lineType: QUOTE_LINE_TYPE.MATERIAL,
    materialId,
  });

  return match?.line_snapshot_id ?? null;
}

export async function resolveLabourQuoteLineSnapshotId(
  projectId,
  timelog = {},
  { quoteLines = null, cache = new Map() } = {},
) {
  if (!projectId) return null;
  if (timelog.quote_line_snapshot_id) return timelog.quote_line_snapshot_id;

  const lines =
    quoteLines ||
    (await loadProjectQuoteLines(projectId, {
      lineType: QUOTE_LINE_TYPE.LABOUR,
    }));

  const match = findMatchingLabourQuoteLine(lines, {
    workCompleted: timelog.work_completed,
    workCategory: timelog.work_category,
    tradeName: timelog.trade_name,
    quoteLineSnapshotId: timelog.quote_line_snapshot_id,
  });

  return match?.line_snapshot_id ?? null;
}

export async function enrichUsageLinesWithQuoteLinks(
  projectId,
  usageLines = [],
  { cache = new Map() } = {},
) {
  if (!projectId || !usageLines.length) return usageLines;

  const quoteLines = await loadProjectQuoteLinesCached(projectId, cache);
  const materialQuoteLines = quoteLines.filter(
    (line) => line.line_type === QUOTE_LINE_TYPE.MATERIAL,
  );

  return usageLines.map((line) => {
    if (line.quote_line_snapshot_id) return line;

    const match = findMatchingQuoteLine(materialQuoteLines, {
      lineType: QUOTE_LINE_TYPE.MATERIAL,
      materialId: line.material_id,
    });

    return {
      ...line,
      quote_line_snapshot_id: match?.line_snapshot_id ?? null,
    };
  });
}

export async function enrichTimelogWithQuoteLink(
  timelog = {},
  { cache = new Map() } = {},
) {
  if (!timelog?.project_id || timelog.quote_line_snapshot_id) {
    return timelog;
  }

  const quoteLineSnapshotId = await resolveLabourQuoteLineSnapshotId(
    timelog.project_id,
    timelog,
    { cache },
  );

  if (!quoteLineSnapshotId) return timelog;

  return {
    ...timelog,
    quote_line_snapshot_id: quoteLineSnapshotId,
  };
}

export async function persistTimelogQuoteLineLink(
  timelogId,
  projectId,
  timelogFields = {},
) {
  if (!timelogId || !projectId || timelogFields.quote_line_snapshot_id) {
    return timelogFields.quote_line_snapshot_id ?? null;
  }

  const quoteLineSnapshotId = await resolveLabourQuoteLineSnapshotId(
    projectId,
    timelogFields,
  );

  if (!quoteLineSnapshotId) return null;

  const { error } = await supabase
    .from("time_logs")
    .update({ quote_line_snapshot_id: quoteLineSnapshotId })
    .eq("id", timelogId)
    .is("quote_line_snapshot_id", null);

  if (error) {
    console.error("TIMELOG QUOTE LINK ERROR:", error);
    return null;
  }

  return quoteLineSnapshotId;
}
